import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Button,
  LinearProgress,
  Link as TextLink,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar/Avatar';
import Helmet from 'react-helmet';
import moment from 'moment';
import { calculateCurrencies } from '@joindeed/currency';
import InfoIcon from '@mui/icons-material/Info';

import { purple } from 'utils/color-styles';
import NonprofitLink from 'retired/NonprofitLink';
import { TableBox } from 'retired/Box';
import { currenciesFormat, currencies } from 'utils/currencyFormat';

import { PartialMatchingModal } from './PartialMatchingModal';

const styles = () => ({
  button: {
    padding: '7px 8px',
    minWidth: 64,
    fontSize: '0.8125rem',
    minHeight: 32,
  },
  nameLink: {
    width: 110,
    display: 'block',
    lineHeight: '32px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
});

const onClickHandler = (e) => e.stopPropagation();

const OverviewTitle = ({ matchingModeText }) => (
  <>
    <h1>{matchingModeText} Donations</h1>
  </>
);
const Overview = ({ matchingMode, closeOverviewAction }) => {
  const matchingModeText = matchingMode ? 'Match' : 'Unmatch';

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <OverviewTitle matchingModeText={matchingModeText}></OverviewTitle>
      <div>
        <Button onClick={closeOverviewAction} size="large" style={{ marginBottom: 10 }}>
          Back
        </Button>
      </div>
    </div>
  );
};

const DonationsOverview = ({
  organization,
  donations,
  classes,
  selectedDonations,
  closeOverviewAction,
  matchDonationsAction,
  unmatchDonationsAction,
  matchingMode,
  donationMatchingRules,
  loading,
}) => {
  const [partialMatchingModalDonation, setPartialMatchingModalDonation] = useState(undefined);

  if (loading) {
    return <LinearProgress />;
  }

  const groupedDonations = donations
    .filter((d) => selectedDonations.has(d.id))
    .groupBy((d) => d.currency);

  const action = matchingMode
    ? (donationToProcess) =>
        matchDonationsAction(
          donationToProcess
            .map((d) => ({
              id: d.get('id'),
              desiredMatchAmount: d.get('desiredMatchAmount'),
              desiredMatchingRule: d.get('desiredMatchingRule') ?? null,
            }))
            .toArray()
        )
    : (donationToProcess) => unmatchDonationsAction(donationToProcess.map((d) => d.get('id')));
  return (
    <>
      <Helmet title="Donations" />
      <Overview matchingMode={matchingMode} closeOverviewAction={closeOverviewAction}></Overview>
      {groupedDonations
        .map((donationsPerCurrency, currencyCode) => {
          const currencySymbol = currencies[currencyCode]?.symbol;
          const matchingTotalAmount = donationsPerCurrency
            .map((donation) => {
              if (matchingMode) {
                return donation.matchableAmountCurrencies;
              }
              return donation.matchedAmountCurrencies;
            })
            .toArray()
            .map((amountCurrencies, index) => {
              if (groupedDonations.toJS()[currencyCode][index].desiredMatchAmount) {
                return groupedDonations.toJS()[currencyCode][index].desiredMatchAmount;
              }
              return amountCurrencies[currencyCode];
            })
            .reduce((a, b) => a + b, 0);

          const matchButtonLabel = `${currencySymbol}${matchingTotalAmount.toLocaleString('en', {
            maximumFractionDigits: 2,
          })}`;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={currencyCode}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 20,
                }}
              >
                <h2>
                  {groupedDonations.size > 1 && (
                    <>{donationsPerCurrency.first()?.currency} donations</>
                  )}
                </h2>
                <div>
                  <Button
                    onClick={() => action(donationsPerCurrency)}
                    color="primary"
                    variant="contained"
                  >
                    {matchingMode ? 'Match' : 'Unmatch'} {donationsPerCurrency.size} donation
                    {donationsPerCurrency.size > 1 ? 's' : ''} - Total: {matchButtonLabel}
                  </Button>
                </div>
              </div>
              <TableBox>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell size="medium">User</TableCell>
                      <TableCell size="medium">
                        {matchingMode ? 'Best Matchable' : 'Matched'} Rule
                      </TableCell>
                      <TableCell size="medium">Donation</TableCell>
                      <TableCell size="medium">Match</TableCell>
                      <TableCell size="medium">Matching %</TableCell>
                      <TableCell size="medium">Date</TableCell>
                      {!matchingMode && <TableCell size="medium">Matched on</TableCell>}
                      <TableCell size="medium">Nonprofits</TableCell>
                      <TableCell size="medium">Deed</TableCell>
                      <TableCell size="medium">Donation partner</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {donationsPerCurrency
                      .map((donation) => {
                        const desiredRule = !donation.desiredMatchingRule
                          ? null
                          : donationMatchingRules?.get(donation.desiredMatchingRule);
                        const rule = matchingMode
                          ? desiredRule ?? donation.matchableRule
                          : donationMatchingRules?.get(donation.matchedRule);
                        const desiredMatch =
                          matchingMode &&
                          donation.desiredMatchAmount !== undefined &&
                          donation.desiredMatchAmount < donation.matchableAmount;
                        const actualMatchPercentage =
                          ((donation.desiredMatchAmount ?? 0) / donation.amount) * 100;
                        const maxMatchingPercentage = rule?.matchingPercentage;
                        return (
                          <TableRow key={donation.id} className={classes.row}>
                            <TableCell>
                              <Avatar
                                alt={donation.user.name}
                                src={
                                  donation.user.mainPicture &&
                                  donation.user.mainPicture.replace('http://', 'https://')
                                }
                                style={{
                                  width: 32,
                                  height: 32,
                                  float: 'left',
                                  marginRight: 10,
                                }}
                              />
                              <Link
                                to={`/volunteers/${donation.user.id}`}
                                className={classes.nameLink}
                                onClick={onClickHandler}
                              >
                                {donation.user.name}
                              </Link>
                            </TableCell>
                            <TableCell>{rule?.title}</TableCell>
                            <TableCell>
                              <Link to={`/donations/${donation.id}`}>
                                {currenciesFormat(donation.amountCurrencies, donation.currency)}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <TextLink
                                component="button"
                                variant="body2"
                                onClick={() => {
                                  if (matchingMode) {
                                    setPartialMatchingModalDonation(donation);
                                  }
                                }}
                                underline="none"
                                tabIndex={0}
                              >
                                {currenciesFormat(
                                  matchingMode
                                    ? desiredMatch
                                      ? calculateCurrencies(
                                          donation.desiredMatchAmount,
                                          donation.currency,
                                          donation.matchableAmountCurrencies
                                        )
                                      : donation.matchableAmountCurrencies
                                    : donation.matchedAmountCurrencies,
                                  donation.currency
                                )}
                              </TextLink>
                              {desiredMatch && (
                                <>
                                  {' '}
                                  of{' '}
                                  {currenciesFormat(
                                    donation.matchableAmountCurrencies,
                                    donation.currency
                                  )}{' '}
                                  (
                                  {Math.round(
                                    (donation.desiredMatchAmount / donation.matchableAmount) * 100
                                  )}
                                  %){' '}
                                  <Tooltip title="Desired partial matching amount" arrow>
                                    <InfoIcon style={{ color: purple, marginTop: -3 }} />
                                  </Tooltip>
                                </>
                              )}
                            </TableCell>
                            <TableCell>
                              {actualMatchPercentage.toFixed(2) === maxMatchingPercentage.toFixed(2)
                                ? `${actualMatchPercentage.toFixed(2)}%`
                                : `${actualMatchPercentage.toFixed(
                                    2
                                  )}% of ${maxMatchingPercentage.toFixed(2)}%`}
                            </TableCell>
                            <TableCell>
                              <Link to={`/donations/${donation.id}`}>
                                <Tooltip title={moment(donation.date).format('HH:mm:ss')}>
                                  <span>{moment(donation.date).format('MMM DD, YYYY')}</span>
                                </Tooltip>
                              </Link>
                            </TableCell>
                            {!matchingMode && (
                              <TableCell>
                                <Link to={`/donations/${donation.id}`}>
                                  {moment(donation.matchedDate).format('MMM DD, YYYY')}
                                </Link>
                              </TableCell>
                            )}
                            <TableCell>
                              {donation.nonprofits.map((nonprofit, i) => (
                                <React.Fragment key={nonprofit.id}>
                                  <NonprofitLink nonprofit={nonprofit} />
                                  {i + 1 < donation.nonprofits.size ? ', ' : ''}
                                </React.Fragment>
                              ))}
                              {
                                donation.paymentMethodDetails?.additionalData?.nonprofit
                                  ?.externalName
                              }
                            </TableCell>
                            <TableCell>
                              {donation.deed &&
                              donation.deed.id &&
                              (donation.deed.organization?.id === organization.id ||
                                donation.deed.partner?.id === organization.id) ? (
                                <Link
                                  to={`/deeds/${donation.deed.pathSlug}/${donation.deed.id}`}
                                  onClick={onClickHandler}
                                >
                                  {donation.deedName}
                                </Link>
                              ) : (
                                donation.deedName
                              )}
                            </TableCell>
                            <TableCell>{donation.providerName}</TableCell>
                          </TableRow>
                        );
                      })
                      .toArray()}
                  </TableBody>
                </Table>
              </TableBox>
              {matchingMode && partialMatchingModalDonation && (
                <PartialMatchingModal
                  donation={partialMatchingModalDonation}
                  onClickClose={() => {
                    setPartialMatchingModalDonation(undefined);
                  }}
                />
              )}
            </div>
          );
        })
        .toArray()}
    </>
  );
};

OverviewTitle.propTypes = {
  matchingModeText: PropTypes.string,
};

Overview.propTypes = {
  matchingMode: PropTypes.bool,
  closeOverviewAction: PropTypes.func,
};

DonationsOverview.propTypes = {
  organization: PropTypes.object,
  donations: PropTypes.object,
  selectedDonations: PropTypes.object,
  donationMatchingRules: PropTypes.object,
  classes: PropTypes.object,
  matchingMode: PropTypes.bool,
  loading: PropTypes.bool,
  closeOverviewAction: PropTypes.func,
  matchDonationsAction: PropTypes.func,
  unmatchDonationsAction: PropTypes.func,
};

export default withStyles(styles)(DonationsOverview);
